import { Box, Icon, IconButton, Tab, Tabs, Tooltip } from "@mui/material";
import { type ReactNode, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import RecordStepper from "~/components/recordStepper/RecordStepper";
import RecordStepperContext from "~/components/recordStepper/RecordStepperContext";
import RecordStepperPrompt from "~/components/recordStepper/RecordStepperPrompt";
import { SubtitleText } from "~/components/Typographies";

export interface DetailPanelTab {
	icon: string;
	label: string;
	tourId?: string;
}

export interface DetailPanelProps {
	actions?: ReactNode;
	activeTab?: number;
	children: ReactNode;
	headerExtra?: ReactNode;
	hideSingleTabStrip?: boolean;
	onClose: () => void;
	onTabChange?: (index: number) => void;
	onTogglePin?: () => void;
	pinned?: boolean;
	tabs?: DetailPanelTab[];
	title: ReactNode;
}

const DetailPanel = ({
	actions,
	activeTab = 0,
	children,
	headerExtra,
	hideSingleTabStrip = false,
	onClose,
	onTabChange,
	onTogglePin,
	pinned,
	tabs = [],
	title
}: DetailPanelProps) => {
	const { t } = useTranslation("common");
	const [closeTooltipOpen, setCloseTooltipOpen] = useState(false);
	const isInStepperScope = useContext(RecordStepperContext) !== null;

	const handleClose = () => {
		setCloseTooltipOpen(false);
		onClose();
	};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
			<Box
				sx={{
					alignItems: "center",
					borderBottom: 1,
					borderBottomColor: "divider",
					display: "flex",
					gap: 1,
					pl: 2,
					pr: 1,
					py: 1
				}}
			>
				{isInStepperScope ? (
					<Box sx={{ alignItems: "center", display: "flex", gap: 1, minWidth: 0, overflow: "hidden" }}>
						{typeof title === "string" ? (
							<SubtitleText bold noWrap>
								{title}
							</SubtitleText>
						) : (
							title
						)}
						{headerExtra}
					</Box>
				) : (
					<>
						{typeof title === "string" ? <SubtitleText bold>{title}</SubtitleText> : title}
						{headerExtra}
					</>
				)}
				<Box sx={{ flex: 1 }} />
				<RecordStepper />
				{onTogglePin && (
					<Tooltip title={pinned ? "Unpin — open on double-click only" : "Pin — follow row clicks"}>
						<IconButton aria-label={pinned ? "unpin panel" : "pin panel"} onClick={onTogglePin} size="small">
							<Icon
								sx={{
									color: pinned ? "primary.main" : "text.secondary",
									fontSize: "1.15rem",
									transform: pinned ? "none" : "rotate(45deg)"
								}}
							>
								push_pin
							</Icon>
						</IconButton>
					</Tooltip>
				)}
				<Tooltip
					onClose={() => setCloseTooltipOpen(false)}
					onOpen={() => setCloseTooltipOpen(true)}
					open={closeTooltipOpen}
					slotProps={{ popper: { popperOptions: { strategy: "fixed" } } }}
					title={t("actions.close")}
				>
					<IconButton aria-label={t("shell.sidePanel.closeAria")} onClick={handleClose} size="small">
						<Icon sx={{ fontSize: "1.15rem" }}>close</Icon>
					</IconButton>
				</Tooltip>
			</Box>
			<RecordStepperPrompt />
			{(!hideSingleTabStrip || tabs.length > 1) && (
				<Box sx={{ backgroundColor: "background.paper", position: "sticky", top: 0, zIndex: 1 }}>
					<Tabs
						onChange={(_, value) => onTabChange?.(value)}
						sx={{ "& .MuiTab-root": { minHeight: 40, minWidth: 0 }, minHeight: 40 }}
						value={activeTab}
						variant="fullWidth"
					>
						{tabs.map((tab) => (
							<Tab
								aria-label={tab.label}
								data-tour-id={tab.tourId}
								icon={
									<Tooltip title={tab.label}>
										<Icon sx={{ fontSize: 20 }}>{tab.icon}</Icon>
									</Tooltip>
								}
								key={tab.label}
								sx={{ p: 1 }}
							/>
						))}
					</Tabs>
				</Box>
			)}
			<Box sx={{ backgroundColor: "background.default", flex: 1, overflow: "auto", p: 1.5 }}>{children}</Box>
			{actions && (
				<Box
					sx={{
						backgroundColor: "background.paper",
						borderTop: 1,
						borderTopColor: "divider",
						display: "flex",
						flexWrap: "wrap",
						gap: 1,
						justifyContent: "flex-end",
						px: 1.5,
						py: 1
					}}
				>
					{actions}
				</Box>
			)}
		</Box>
	);
};

export default DetailPanel;
